import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Pencil, Trash2, FileText, Search, Eye, Printer, X } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnlineBuiltyPage = () => {
  const { user } = useAuth();
  const [builties, setBuilties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [selectedBuilty, setSelectedBuilty] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);

  const initialFormData = {
    date: new Date().toISOString().split("T")[0],
    branch_code: "01",
    transport_mode: "By Road",
    delivery_type: "Door",
    payment_status: "To Pay",
    insurance_status: "Not Insured",
    eway_bill_no: "",
    invoice_no: "",
    vehicle_no: "",
    driver_name: "",
    driver_mobile: "",
    consignor_name: "",
    consignor_gst: "",
    consignor_mobile: "",
    consignor_address: "",
    consignee_name: "",
    consignee_gst: "",
    consignee_mobile: "",
    consignee_address: "",
    consignee_pan: "",
    consignee_uin: "",
    origin: "",
    destination: "",
    goods_items: [],
    risk_type: "Owner's Risk",
    total_basic_freight: 0,
    packing_charge: 0,
    pickup_delivery_charge: 0,
    service_charge: 0,
    loading_unloading_charge: 0,
    cod_dod_charge: 0,
    other_charges: 0,
    sgst_percent: 0,
    cgst_percent: 0,
    advance_paid: 0,
    gst_payable_by: "Consignee",
    remaining_payable_by: "Consignee",
    bank_name: "",
    bank_account_no: "",
    bank_ifsc: "",
    service_area: "",
    demurrage_free_hours: 1,
    demurrage_charge_per_hour: 0,
    authorized_signatory_name: "",
    authorized_signatory_designation: "",
    other_remark: "",
    terms_conditions: "",
    other_information: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [goodsItem, setGoodsItem] = useState({
    sr_no: 1,
    product_material: "",
    packaging_type: "",
    hsn_code: "",
    articles: "",
    packages: 0,
    actual_weight: 0,
    chargeable_weight: 0,
    freight_rate: 0,
  });

  const fetchBuilties = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/builties`);
      setBuilties(response.data);
    } catch (error) {
      toast.error("Failed to fetch builties");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients");
    }
  }, []);

  useEffect(() => {
    fetchBuilties();
    fetchClients();
  }, [fetchBuilties, fetchClients]);

  const calculateTotals = (data) => {
    const subtotal = 
      parseFloat(data.total_basic_freight || 0) +
      parseFloat(data.packing_charge || 0) +
      parseFloat(data.pickup_delivery_charge || 0) +
      parseFloat(data.service_charge || 0) +
      parseFloat(data.loading_unloading_charge || 0) +
      parseFloat(data.cod_dod_charge || 0) +
      parseFloat(data.other_charges || 0);
    
    const sgstAmount = subtotal * (parseFloat(data.sgst_percent || 0) / 100);
    const cgstAmount = subtotal * (parseFloat(data.cgst_percent || 0) / 100);
    const totalFreight = subtotal + sgstAmount + cgstAmount;
    const remainingAmount = totalFreight - parseFloat(data.advance_paid || 0);

    return { subtotal, sgstAmount, cgstAmount, totalFreight, remainingAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consignor_name || !formData.consignee_name || !formData.origin || !formData.destination) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const submitData = { ...formData };
      
      // Auto-add any pending goods item if the user filled in product details but forgot to click "Add Item"
      if (goodsItem.product_material && goodsItem.product_material.trim() !== '') {
        const pendingItem = {
          ...goodsItem,
          sr_no: submitData.goods_items.length + 1,
        };
        submitData.goods_items = [...submitData.goods_items, pendingItem];
      }
      
      if (selectedBuilty) {
        await axios.put(`${API_URL}/builties/${selectedBuilty.id}`, submitData);
        toast.success("Builty updated successfully");
      } else {
        await axios.post(`${API_URL}/builties`, submitData);
        toast.success("Builty created successfully");
      }
      
      setDialogOpen(false);
      setSelectedBuilty(null);
      setFormData(initialFormData);
      setGoodsItem({
        sr_no: 1,
        product_material: "",
        packaging_type: "",
        hsn_code: "",
        articles: "",
        packages: 0,
        actual_weight: 0,
        chargeable_weight: 0,
        freight_rate: 0,
      });
      fetchBuilties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save builty");
    }
  };

  const handleEdit = (builty) => {
    setSelectedBuilty(builty);
    setFormData({
      ...initialFormData,
      ...builty,
      goods_items: builty.goods_items || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this builty?")) return;
    
    try {
      await axios.delete(`${API_URL}/builties/${id}`);
      toast.success("Builty deleted successfully");
      fetchBuilties();
    } catch (error) {
      toast.error("Failed to delete builty");
    }
  };

  const handlePreview = async (builty) => {
    try {
      const response = await axios.get(`${API_URL}/builties/${builty.id}/html`);
      setPreviewHtml(response.data);
      setSelectedBuilty(builty);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load preview");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const addGoodsItem = () => {
    const newItem = {
      ...goodsItem,
      sr_no: formData.goods_items.length + 1,
    };
    setFormData({
      ...formData,
      goods_items: [...formData.goods_items, newItem],
    });
    setGoodsItem({
      sr_no: formData.goods_items.length + 2,
      product_material: "",
      packaging_type: "",
      hsn_code: "",
      articles: "",
      packages: 0,
      actual_weight: 0,
      chargeable_weight: 0,
      freight_rate: 0,
    });
  };

  const removeGoodsItem = (index) => {
    const newItems = formData.goods_items.filter((_, i) => i !== index);
    newItems.forEach((item, i) => item.sr_no = i + 1);
    setFormData({ ...formData, goods_items: newItems });
  };

  const filteredBuilties = builties.filter(b =>
    b.builty_number?.toString().includes(searchTerm) ||
    b.consignor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.consignee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.destination?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals(formData);

  return (
    <div className="space-y-6" data-testid="online-builty-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Online Builty</h1>
            <p className="text-sm text-muted-foreground">
              Create and manage Lorry Receipts (LR/Builty)
            </p>
          </div>
        </div>
        <Button onClick={() => { setSelectedBuilty(null); setFormData(initialFormData); setDialogOpen(true); }} data-testid="create-builty-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Builty
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search by number, party, or route..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-builty"
        />
      </div>

      {/* Table */}
      <div className="glass rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border/50">
          <h3 className="font-heading font-semibold text-foreground">
            All Builties ({filteredBuilties.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border/50">
                <TableHead className="text-muted-foreground">Builty No.</TableHead>
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">Consignor</TableHead>
                <TableHead className="text-muted-foreground">Consignee</TableHead>
                <TableHead className="text-muted-foreground">Route</TableHead>
                <TableHead className="text-muted-foreground">Amount</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredBuilties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No builties found
                  </TableCell>
                </TableRow>
              ) : (
                filteredBuilties.map((builty) => (
                  <TableRow key={builty.id} className="border-border/50">
                    <TableCell className="font-mono font-semibold text-primary">
                      {builty.builty_number}
                    </TableCell>
                    <TableCell>{builty.date}</TableCell>
                    <TableCell>{builty.consignor_name}</TableCell>
                    <TableCell>{builty.consignee_name}</TableCell>
                    <TableCell>
                      <span className="text-xs">{builty.origin} → {builty.destination}</span>
                    </TableCell>
                    <TableCell className="font-semibold">
                      ₹{builty.total_freight?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell>
                      <Badge variant={builty.payment_status === "Paid" ? "default" : "secondary"}>
                        {builty.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(builty)}
                          data-testid={`preview-builty-${builty.id}`}
                          title="Preview & Print"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(builty)}
                          data-testid={`edit-builty-${builty.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {(user?.role === "admin" || user?.role === "super_admin") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(builty.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-builty-${builty.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedBuilty ? "Edit Builty" : "Create New Builty"}
            </DialogTitle>
            <DialogDescription>
              Fill in the details to {selectedBuilty ? "update" : "create"} a Lorry Receipt
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="branch_code">Branch Code</Label>
                <Input
                  id="branch_code"
                  value={formData.branch_code}
                  onChange={(e) => setFormData({ ...formData, branch_code: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="transport_mode">Transport Mode</Label>
                <Select value={formData.transport_mode} onValueChange={(v) => setFormData({ ...formData, transport_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="By Road">By Road</SelectItem>
                    <SelectItem value="By Rail">By Rail</SelectItem>
                    <SelectItem value="By Air">By Air</SelectItem>
                    <SelectItem value="By Sea">By Sea</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="delivery_type">Delivery Type</Label>
                <Select value={formData.delivery_type} onValueChange={(v) => setFormData({ ...formData, delivery_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Door">Door Delivery</SelectItem>
                    <SelectItem value="Godown">Godown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment_status">Payment Status</Label>
                <Select value={formData.payment_status} onValueChange={(v) => setFormData({ ...formData, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="To Pay">To Pay</SelectItem>
                    <SelectItem value="TBB">TBB (To Be Billed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="risk_type">Risk Type</Label>
                <Select value={formData.risk_type} onValueChange={(v) => setFormData({ ...formData, risk_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Owner's Risk">Owner&apos;s Risk</SelectItem>
                    <SelectItem value="Carrier's Risk">Carrier&apos;s Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="insurance_status">Insurance Status</Label>
                <Input
                  id="insurance_status"
                  value={formData.insurance_status}
                  onChange={(e) => setFormData({ ...formData, insurance_status: e.target.value })}
                  placeholder="Not Insured / Insured with..."
                />
              </div>
            </div>

            {/* Vehicle & Document Details */}
            <div className="space-y-3 p-4 border rounded-lg bg-blue-950/30">
              <h4 className="font-semibold text-primary">Vehicle & Document Details</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Vehicle No. *</Label>
                  <Input
                    value={formData.vehicle_no}
                    onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value.toUpperCase() })}
                    placeholder="MH12AB1234"
                  />
                </div>
                <div>
                  <Label>E-Way Bill No.</Label>
                  <Input
                    value={formData.eway_bill_no}
                    onChange={(e) => setFormData({ ...formData, eway_bill_no: e.target.value })}
                    placeholder="E-Way Bill Number"
                  />
                </div>
                <div>
                  <Label>Invoice No.</Label>
                  <Input
                    value={formData.invoice_no}
                    onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })}
                    placeholder="Invoice Number"
                  />
                </div>
                <div>
                  <Label>Driver Name</Label>
                  <Input
                    value={formData.driver_name}
                    onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                    placeholder="Driver Name"
                  />
                </div>
                <div>
                  <Label>Driver Mobile</Label>
                  <Input
                    value={formData.driver_mobile}
                    onChange={(e) => setFormData({ ...formData, driver_mobile: e.target.value })}
                    placeholder="Driver Mobile Number"
                  />
                </div>
              </div>
            </div>

            {/* Consignor & Consignee */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Consignor */}
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-primary">Consignor Details</h4>
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.consignor_name}
                    onChange={(e) => setFormData({ ...formData, consignor_name: e.target.value })}
                    placeholder="Consignor Name"
                    required
                  />
                </div>
                <div>
                  <Label>GST No.</Label>
                  <Input
                    value={formData.consignor_gst}
                    onChange={(e) => setFormData({ ...formData, consignor_gst: e.target.value })}
                    placeholder="GST Number"
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={formData.consignor_mobile}
                    onChange={(e) => setFormData({ ...formData, consignor_mobile: e.target.value })}
                    placeholder="Mobile Number"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={formData.consignor_address}
                    onChange={(e) => setFormData({ ...formData, consignor_address: e.target.value })}
                    placeholder="Full Address"
                    rows={2}
                  />
                </div>
              </div>

              {/* Consignee */}
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-primary">Consignee Details</h4>
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.consignee_name}
                    onChange={(e) => setFormData({ ...formData, consignee_name: e.target.value })}
                    placeholder="Consignee Name"
                    required
                  />
                </div>
                <div>
                  <Label>GST No.</Label>
                  <Input
                    value={formData.consignee_gst}
                    onChange={(e) => setFormData({ ...formData, consignee_gst: e.target.value })}
                    placeholder="GST Number"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Mobile</Label>
                    <Input
                      value={formData.consignee_mobile}
                      onChange={(e) => setFormData({ ...formData, consignee_mobile: e.target.value })}
                      placeholder="Mobile"
                    />
                  </div>
                  <div>
                    <Label>PAN No.</Label>
                    <Input
                      value={formData.consignee_pan}
                      onChange={(e) => setFormData({ ...formData, consignee_pan: e.target.value })}
                      placeholder="PAN Number"
                    />
                  </div>
                </div>
                <div>
                  <Label>UIN No.</Label>
                  <Input
                    value={formData.consignee_uin}
                    onChange={(e) => setFormData({ ...formData, consignee_uin: e.target.value })}
                    placeholder="UIN Number"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={formData.consignee_address}
                    onChange={(e) => setFormData({ ...formData, consignee_address: e.target.value })}
                    placeholder="Full Address"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Route */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Origin *</Label>
                <Input
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="From (City/Location)"
                  required
                />
              </div>
              <div>
                <Label>Destination *</Label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  placeholder="To (City/Location)"
                  required
                />
              </div>
            </div>

            {/* Goods Items */}
            <div className="space-y-3 p-4 border rounded-lg">
              <h4 className="font-semibold text-primary">Goods Details</h4>
              
              {/* Existing Items */}
              {formData.goods_items.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Sr</TableHead>
                        <TableHead>Product/Material</TableHead>
                        <TableHead>Packaging</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead>Pkgs</TableHead>
                        <TableHead>Wt.</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.goods_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.sr_no}</TableCell>
                          <TableCell>{item.product_material}</TableCell>
                          <TableCell>{item.packaging_type}</TableCell>
                          <TableCell>{item.hsn_code}</TableCell>
                          <TableCell>{item.packages}</TableCell>
                          <TableCell>{item.chargeable_weight}</TableCell>
                          <TableCell>{item.freight_rate}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeGoodsItem(index)}
                              className="text-destructive"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* No items message */}
              {formData.goods_items.length === 0 && (
                <div className="text-center py-3 text-muted-foreground text-sm bg-secondary/20 rounded">
                  No goods items added yet. Fill in the details below and click &quot;Add Item&quot;
                </div>
              )}
              
              {/* Add New Item Form */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-blue-950/30 rounded-lg border border-blue-500/30">
                <Input
                  placeholder="Product/Material *"
                  value={goodsItem.product_material}
                  onChange={(e) => setGoodsItem({ ...goodsItem, product_material: e.target.value })}
                  className={goodsItem.product_material ? "border-green-500" : ""}
                />
                <Input
                  placeholder="Packaging (LxBxH)"
                  value={goodsItem.packaging_type}
                  onChange={(e) => setGoodsItem({ ...goodsItem, packaging_type: e.target.value })}
                />
                <Input
                  placeholder="HSN Code"
                  value={goodsItem.hsn_code}
                  onChange={(e) => setGoodsItem({ ...goodsItem, hsn_code: e.target.value })}
                />
                <Input
                  placeholder="Articles"
                  value={goodsItem.articles}
                  onChange={(e) => setGoodsItem({ ...goodsItem, articles: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Packages"
                  value={goodsItem.packages || ""}
                  onChange={(e) => setGoodsItem({ ...goodsItem, packages: parseInt(e.target.value) || 0 })}
                />
                <Input
                  type="number"
                  placeholder="Actual Weight"
                  value={goodsItem.actual_weight || ""}
                  onChange={(e) => setGoodsItem({ ...goodsItem, actual_weight: parseFloat(e.target.value) || 0 })}
                />
                <Input
                  type="number"
                  placeholder="Chargeable Weight"
                  value={goodsItem.chargeable_weight || ""}
                  onChange={(e) => setGoodsItem({ ...goodsItem, chargeable_weight: parseFloat(e.target.value) || 0 })}
                />
                <Input
                  type="number"
                  placeholder="Freight Rate"
                  value={goodsItem.freight_rate || ""}
                  onChange={(e) => setGoodsItem({ ...goodsItem, freight_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <Button 
                type="button" 
                variant={goodsItem.product_material ? "default" : "outline"} 
                size="sm" 
                onClick={addGoodsItem}
                disabled={!goodsItem.product_material}
                className={goodsItem.product_material ? "bg-green-600 hover:bg-green-700" : ""}
              >
                <Plus className="w-4 h-4 mr-2" />
                {goodsItem.product_material ? `Add "${goodsItem.product_material}" to List` : "Add Item"}
              </Button>
              {goodsItem.product_material && (
                <p className="text-xs text-amber-400">
                  ⚠️ Don&apos;t forget to click the button above, or the item will be auto-added when you save
                </p>
              )}
            </div>

            {/* Charges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border rounded-lg">
              <h4 className="font-semibold text-primary col-span-full">Charges</h4>
              <div>
                <Label>Total Basic Freight (₹)</Label>
                <Input
                  type="number"
                  value={formData.total_basic_freight || ""}
                  onChange={(e) => setFormData({ ...formData, total_basic_freight: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Packing & Unpacking (₹)</Label>
                <Input
                  type="number"
                  value={formData.packing_charge || ""}
                  onChange={(e) => setFormData({ ...formData, packing_charge: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Pickup & Delivery (₹)</Label>
                <Input
                  type="number"
                  value={formData.pickup_delivery_charge || ""}
                  onChange={(e) => setFormData({ ...formData, pickup_delivery_charge: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Service Charge (₹)</Label>
                <Input
                  type="number"
                  value={formData.service_charge || ""}
                  onChange={(e) => setFormData({ ...formData, service_charge: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Loading/Unloading (₹)</Label>
                <Input
                  type="number"
                  value={formData.loading_unloading_charge || ""}
                  onChange={(e) => setFormData({ ...formData, loading_unloading_charge: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>COD/DOD Charges (₹)</Label>
                <Input
                  type="number"
                  value={formData.cod_dod_charge || ""}
                  onChange={(e) => setFormData({ ...formData, cod_dod_charge: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Other Charges (₹)</Label>
                <Input
                  type="number"
                  value={formData.other_charges || ""}
                  onChange={(e) => setFormData({ ...formData, other_charges: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Advance Paid (₹)</Label>
                <Input
                  type="number"
                  value={formData.advance_paid || ""}
                  onChange={(e) => setFormData({ ...formData, advance_paid: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>SGST %</Label>
                <Input
                  type="number"
                  value={formData.sgst_percent || ""}
                  onChange={(e) => setFormData({ ...formData, sgst_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>CGST %</Label>
                <Input
                  type="number"
                  value={formData.cgst_percent || ""}
                  onChange={(e) => setFormData({ ...formData, cgst_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>GST Payable By</Label>
                <Select value={formData.gst_payable_by} onValueChange={(v) => setFormData({ ...formData, gst_payable_by: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Consignor">Consignor</SelectItem>
                    <SelectItem value="Consignee">Consignee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remaining Payable By</Label>
                <Select value={formData.remaining_payable_by} onValueChange={(v) => setFormData({ ...formData, remaining_payable_by: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Consignor">Consignor</SelectItem>
                    <SelectItem value="Consignee">Consignee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Totals Display */}
            <div className="p-4 bg-primary/10 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="ml-2 font-semibold">₹{totals.subtotal.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">SGST:</span>
                  <span className="ml-2 font-semibold">₹{totals.sgstAmount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">CGST:</span>
                  <span className="ml-2 font-semibold">₹{totals.cgstAmount.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Freight:</span>
                  <span className="ml-2 font-bold text-primary">₹{totals.totalFreight.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Balance:</span>
                  <span className="ml-2 font-bold text-green-600">₹{totals.remainingAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Bank & Additional Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-primary">Bank Details</h4>
                <Input
                  placeholder="Bank Name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                />
                <Input
                  placeholder="Account Number"
                  value={formData.bank_account_no}
                  onChange={(e) => setFormData({ ...formData, bank_account_no: e.target.value })}
                />
                <Input
                  placeholder="IFSC Code"
                  value={formData.bank_ifsc}
                  onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                />
              </div>
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-primary">Service Area</h4>
                <Textarea
                  placeholder="e.g., ASSAM, BENGAL, BIHAR..."
                  value={formData.service_area}
                  onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-primary">Authorized Signatory</h4>
                <Input
                  placeholder="Name"
                  value={formData.authorized_signatory_name}
                  onChange={(e) => setFormData({ ...formData, authorized_signatory_name: e.target.value })}
                />
                <Input
                  placeholder="Designation"
                  value={formData.authorized_signatory_designation}
                  onChange={(e) => setFormData({ ...formData, authorized_signatory_designation: e.target.value })}
                />
              </div>
            </div>

            {/* Demurrage */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Demurrage Free Hours</Label>
                <Input
                  type="number"
                  value={formData.demurrage_free_hours || ""}
                  onChange={(e) => setFormData({ ...formData, demurrage_free_hours: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Demurrage Charge (₹/Hour)</Label>
                <Input
                  type="number"
                  value={formData.demurrage_charge_per_hour || ""}
                  onChange={(e) => setFormData({ ...formData, demurrage_charge_per_hour: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <Label>Other Remarks</Label>
              <Textarea
                value={formData.other_remark}
                onChange={(e) => setFormData({ ...formData, other_remark: e.target.value })}
                placeholder="Additional remarks..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-builty-btn">
                {selectedBuilty ? "Update Builty" : "Create Builty"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-white text-black max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-black flex items-center justify-between">
              <span>Builty Preview - {selectedBuilty?.builty_number}</span>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-builty">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnlineBuiltyPage;
